/**
 * Toast Component - Premium Non-blocking Notifications
 * Top-right placement, auto-dismiss, smooth transitions
 * Replaces window.alert with modern UX
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CloseOutlined, CheckCircleOutlined, WarningOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { colors, borderRadius, shadows } from '../../theme/designTokens';

// Toast container styles
const containerStyle = {
  position: 'fixed',
  top: 16,
  right: 16,
  zIndex: 2000, // Above modals
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  maxWidth: 400,
};

// Single toast styles
const getToastStyle = (isExiting) => ({
  backgroundColor: '#FFFFFF',
  color: '#17052B',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  borderLeft: `3px solid ${colors.primary[500]}`,
  borderRadius: borderRadius.md,
  padding: '12px 16px',
  display: 'flex',
  gap: 12,
  alignItems: 'flex-start',
  minWidth: 320,
  maxWidth: 400,
  animation: isExiting ? 'toastSlideOut 300ms ease-out forwards' : 'toastSlideIn 300ms ease-out',
  opacity: isExiting ? 0 : 1,
  transform: isExiting ? 'translateX(100%)' : 'translateX(0)',
  transition: 'opacity 300ms ease-out, transform 300ms ease-out',
});

// Toast manager singleton
class ToastManager {
  constructor() {
    this.toasts = [];
    this.listeners = [];
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  notify() {
    this.listeners.forEach((listener) => listener(this.toasts));
  }

  show(config) {
    const id = Date.now() + Math.random();
    const toast = { id, ...config, isExiting: false };
    this.toasts.push(toast);
    this.notify();

    // Auto-dismiss after duration
    setTimeout(() => {
      this.dismiss(id);
    }, config.duration || 2500);

    return id;
  }

  dismiss(id) {
    // Mark as exiting for animation
    this.toasts = this.toasts.map((t) =>
      t.id === id ? { ...t, isExiting: true } : t
    );
    this.notify();

    // Remove after animation
    setTimeout(() => {
      this.toasts = this.toasts.filter((t) => t.id !== id);
      this.notify();
    }, 300);
  }

  success(title, message, duration) {
    return this.show({ type: 'success', title, message, duration });
  }

  error(title, message, duration) {
    return this.show({ type: 'error', title, message, duration });
  }

  warning(title, message, duration) {
    return this.show({ type: 'warning', title, message, duration });
  }

  info(title, message, duration) {
    return this.show({ type: 'info', title, message, duration });
  }
}

export const toastManager = new ToastManager();

/**
 * Individual Toast Component
 */
function ToastItem({ toast, onClose }) {
  const { type, title, message, isExiting } = toast;

  const icons = {
    success: <CheckCircleOutlined style={{ color: colors.status.pass, fontSize: 18 }} />,
    error: <WarningOutlined style={{ color: colors.status.fail, fontSize: 18 }} />,
    warning: <WarningOutlined style={{ color: colors.severity.medium.main, fontSize: 18 }} />,
    info: <InfoCircleOutlined style={{ color: colors.status.info, fontSize: 18 }} />,
  };

  return (
    <div style={getToastStyle(isExiting)}>
      <div style={{ flexShrink: 0, marginTop: 2 }}>
        {icons[type] || icons.info}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: message ? 4 : 0 }}>
          {title}
        </div>
        {message && (
          <div style={{ fontSize: 13, color: 'rgba(23, 5, 43, 0.75)', lineHeight: 1.4 }}>
            {message}
          </div>
        )}
      </div>
      <button
        onClick={onClose}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'rgba(23, 5, 43, 0.5)',
          flexShrink: 0,
        }}
      >
        <CloseOutlined style={{ fontSize: 12 }} />
      </button>
    </div>
  );
}

/**
 * Toast Container Component
 */
function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const unsubscribe = toastManager.subscribe((newToasts) => {
      setToasts([...newToasts]);
    });
    return unsubscribe;
  }, []);

  if (toasts.length === 0) return null;

  return createPortal(
    <>
      <style>
        {`
          @keyframes toastSlideIn {
            from {
              opacity: 0;
              transform: translateX(100%);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }

          @keyframes toastSlideOut {
            from {
              opacity: 1;
              transform: translateX(0);
            }
            to {
              opacity: 0;
              transform: translateX(100%);
            }
          }
        `}
      </style>
      <div style={containerStyle}>
        {toasts.map((toast) => (
          <ToastItem
            key={toast.id}
            toast={toast}
            onClose={() => toastManager.dismiss(toast.id)}
          />
        ))}
      </div>
    </>,
    document.body
  );
}

/**
 * Utility functions for easy usage
 */
export const toast = {
  success: (title, message, duration) => toastManager.success(title, message, duration),
  error: (title, message, duration) => toastManager.error(title, message, duration),
  warning: (title, message, duration) => toastManager.warning(title, message, duration),
  info: (title, message, duration) => toastManager.info(title, message, duration),
};

export default ToastContainer;
